import React from 'react';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react';
import { DateTime } from 'luxon';
import { routes } from '@portal/env/routes';
import { formatAddress } from '@portal/env/utils/eventDetailsHelpers';
import { DateTimeIcon, LocationIcon } from '@common/bw-ui/icons';
import { Event } from '@portal/types/nexudus/events';
import { Card } from '@common/bw-ui/ui/card';
import { documentToPlainTextString } from '@portal/env/utils/textHelpers';
import { useApp } from '../hooks/useApp';

const FeaturedEventCard: React.FC<{ featuredEvent: Event }> = observer(
  ({ featuredEvent }) => {
    const { resourceImageBaseUrl } = useApp();
    const router = useRouter();

    const start = DateTime.fromISO(featuredEvent.StartDate);
    const end = DateTime.fromISO(
      featuredEvent.EndDate || featuredEvent.StartDate,
    );
    const isSameDay = start.hasSame(end, 'day');
    const shortDateLabel = start.toFormat('d LLL');

    const datetimeLabel = isSameDay
      ? `${start.toFormat('HH:mm')} - ${end.toFormat('HH:mm')}`
      : `${start.toFormat('d LLL HH:mm')} - ${end.toFormat('d LLL HH:mm')}`;

    const formattedVenueAddress = featuredEvent
      ? formatAddress(featuredEvent.VenueAddress)
      : '';

    if (!featuredEvent) return null;

    const shortDescription = documentToPlainTextString(
      featuredEvent.ShortDescription || '',
    );

    return (
      <Card
        className="tw:grid tw:grid-cols-1 tw:grid-rows-[auto_1fr] tw:md:grid-cols-2 tw:md:grid-rows-none tw:relative tw:w-full tw:border tw:lg:border-none tw:border-gray-200 tw:overflow-hidden tw:shadow-none tw:cursor-pointer"
        onClick={() => router.push(routes.event_by_id(featuredEvent))}
      >
        <div className="tw:relative tw:w-full tw:aspect-[16/9] tw:rounded-t-lg tw:overflow-hidden tw:md:rounded-l-lg tw:md:rounded-tr-none tw:md:h-full tw:flex-shrink-0">
          <img
            src={`${resourceImageBaseUrl}/events/getlargeimage?id=${featuredEvent.Id}&w=800&h=400&mode=Crop&cache=${featuredEvent.UpdatedOn}`}
            alt={featuredEvent.Name}
            className="tw:w-full tw:h-full tw:object-cover"
          />
          <span className="tw:absolute tw:bottom-3 tw:left-4 tw:bg-white tw:text-black tw:px-2 tw:text-xs tw:font-normal tw:py-1 tw:rounded">
            {shortDateLabel}
          </span>
        </div>

        <div className="tw:flex tw:flex-col tw:flex-1 tw:p-4 tw:lg:mt-4">
          <div className="tw:flex tw:flex-col tw:gap-1">
            <span className="tw:bg-primary tw:w-fit tw:text-white tw:px-3 tw:md:my-4 tw:py-1 tw:rounded tw:text-xs tw:lg:text-sm tw:font-normal">
              Featured
            </span>
            <h4 className="tw:text-lg tw:lg:text-2xl tw:my-0 tw:font-medium tw:line-clamp-2">
              {featuredEvent.Name}
            </h4>
          </div>
          <p className="tw:prose tw:prose-slate tw:max-w-none tw:text-slate-900 tw:text-sm tw:my-2 tw:md:my-4 tw:font-light tw:line-clamp-2">
            {shortDescription}
          </p>

          <div className="tw:flex tw:flex-col tw:gap-2 tw:mb-4 tw:mt-0">
            {formattedVenueAddress && (
              <div className="tw:flex tw:items-center tw:gap-2">
                <LocationIcon className="tw:w-6 tw:h-6 tw:text-primary" />
                <p className="tw:text-gray-500 tw:font-normal tw:text-xs tw:m-0">
                  {formattedVenueAddress}
                </p>
              </div>
            )}
            <div className="tw:flex tw:items-center tw:gap-2">
              <DateTimeIcon className="tw:w-6 tw:h-6 tw:text-primary" />
              <div className="tw:flex tw:flex-col">
                <p className="tw:text-xs tw:font-normal tw:text-gray-500 tw:m-0">
                  {datetimeLabel}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  },
);

export default FeaturedEventCard;
